export const CACHE_AUDIO = 'archives-offline-audio-v1';
const MANIFEST_KEY = 'archives-offline-manifest-v1';

// In-memory mirror of the manifest — authoritative within the session.
// localStorage is used for cross-session persistence when available.
let _manifest = null;

function readManifest() {
  if (_manifest !== null) return _manifest;
  try {
    const raw = localStorage.getItem(MANIFEST_KEY);
    _manifest = raw ? JSON.parse(raw) : {};
  } catch (_) {
    _manifest = {};
  }
  return _manifest;
}

function writeManifest(m) {
  _manifest = m;
  try { localStorage.setItem(MANIFEST_KEY, JSON.stringify(m)); } catch (_) {}
}

// Reset in-memory cache when localStorage.clear() is called (test teardown).
// We patch this by hooking into the module lifecycle: export a reset for tests,
// but more simply: re-read from localStorage each call when _manifest is null.
// Tests call localStorage.clear() which is a jest.fn() no-op, so we rely on
// the test's afterEach to re-init. We expose a resetManifest for test use.
export function _resetManifest() { _manifest = null; }

export function getManifest() { return readManifest(); }
export function getDownloadedIds() { return Object.keys(readManifest()); }
export function isEpisodeDownloaded(id) { return readManifest()[id] !== undefined; }

// Return a blob: object URL for a downloaded episode's audio, read straight
// from the offline cache. Playing from this complete in-memory blob avoids
// routing media through the service worker (whose range-serving of cached
// audio stalls playback mid-track). Returns null when the episode isn't
// downloaded or the cache entry is missing. The CALLER owns the URL and must
// URL.revokeObjectURL() it when finished.
export async function getOfflineObjectUrl(episode) {
  if (typeof caches === 'undefined' || !episode) return null;
  const entry = readManifest()[episode.id];
  if (!entry) return null;
  try {
    const cache = await caches.open(CACHE_AUDIO);
    const res = await cache.match(entry.url || episode.s3Url);
    if (!res) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (_) {
    return null;
  }
}

// Normalizes a caught error into a throw: an AbortError propagates
// untouched (so callers can distinguish "user cancelled" from a real
// failure), anything else becomes a plain Error with a descriptive message.
function rethrowAsDownloadFailure(e) {
  if (e && e.name === 'AbortError') throw e;
  throw new Error(`Failed to download episode: ${(e && e.message) || 'unknown error'}`);
}

export async function downloadEpisode(episode, onProgress, signal) {
  if (typeof caches === 'undefined') {
    throw new Error('Offline storage is not supported in this browser');
  }

  let response;
  try {
    response = await fetch(episode.s3Url, { mode: 'cors', cache: 'reload', signal });
  } catch (e) {
    rethrowAsDownloadFailure(e);
  }
  if (!response || !response.ok) {
    throw new Error(`Failed to download episode: HTTP ${response ? response.status : 'error'}`);
  }

  const total = Number(response.headers.get('content-length')) || 0;
  const contentType = response.headers.get('content-type') || 'audio/mpeg';
  const chunks = [];
  let received = 0;

  const reader = response.body && response.body.getReader ? response.body.getReader() : null;
  if (reader) {
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (onProgress) onProgress({ received, total, ratio: total ? received / total : null });
      }
    } catch (e) {
      rethrowAsDownloadFailure(e);
    }
  }

  const blob = new Blob(chunks, { type: contentType });
  const cache = await caches.open(CACHE_AUDIO);
  await cache.put(
    episode.s3Url,
    new Response(blob, { status: 200, headers: { 'Content-Type': contentType, 'Content-Length': String(blob.size || received) } })
  );

  const manifest = readManifest();
  manifest[episode.id] = {
    downloadedAt: new Date().toISOString(),
    bytes: blob.size || received,
    title: episode.title,
    url: episode.s3Url,
  };
  writeManifest(manifest);
}

export async function removeEpisode(id) {
  const manifest = readManifest();
  const entry = manifest[id];
  if (!entry) return;
  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(CACHE_AUDIO);
      await cache.delete(entry.url);
    } catch (_) {}
  }
  delete manifest[id];
  writeManifest(manifest);
}

export async function removeAll() {
  if (typeof caches !== 'undefined') {
    try { await caches.delete(CACHE_AUDIO); } catch (_) {}
  }
  writeManifest({});
}

export async function estimateStorage() {
  if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
    return { usage: 0, quota: 0 };
  }
  try {
    const est = await navigator.storage.estimate();
    return { usage: est.usage || 0, quota: est.quota || 0 };
  } catch (_) {
    return { usage: 0, quota: 0 };
  }
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
